import { Flex, Modal, toast } from '@solinkcorp/solink-components';
import PlatformApi, { type IEvent } from 'clients/PlatformApi';
import CenteredLoadingSpinner from 'loadingIndicator/CenteredLoadingSpinner';
import React, { useCallback } from 'react';
import useLocale from 'src/hooks/useLocale';
import styled from 'styled-components';
import * as EventUtil from 'utils/EventUtil';
import LocaleUtil from 'utils/LocaleUtil';
import { FAST_NOTIFICATION_DURATION } from 'vd-constants';

interface ArchivePanelProps {
  archivePanelOpen?: boolean;
  closeArchivePanel?: () => void;
  event: IEvent;
  type: 'archive' | 'unarchive';
  additionalArchiveActions?: () => void;
  additionalUnarchiveActions?: () => void;
  removeEvent?: (targetEvent: IEvent) => void;
}

const ArchivePanel: React.FC<ArchivePanelProps> = (props) => {
  const {
    archivePanelOpen,
    event,
    type,
    closeArchivePanel,
    additionalArchiveActions,
    additionalUnarchiveActions,
    removeEvent,
  } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const locale = useLocale();

  const isArchiveContext = type === 'archive';

  const modalCloseAction = useCallback(() => {
    if (closeArchivePanel) {
      closeArchivePanel();
    }
  }, [closeArchivePanel]);

  const archiveEvent = async () => {
    const { title, id, shares } = event;
    setIsLoading(true);
    try {
      await PlatformApi().bulkArchiveEvents([
        { id, shares: EventUtil.getEventClosedShares(shares) },
      ]);

      toast.success(
        LocaleUtil.getLocalizedStr({
          string: locale.archive.archiveNotificationMessage,
          replacements: { title },
        }),
      );

      additionalArchiveActions?.();
      removeEvent?.(event);
      closeArchivePanel?.();
    } catch (error) {
      console.error(error);
      toast.error(locale.archive.errorToast);
    } finally {
      setIsLoading(false);
    }
  };

  const unarchiveEvent = async () => {
    const { title, id } = event;
    setIsLoading(true);
    try {
      await PlatformApi().unarchiveEvent(id);
      toast.success(
        LocaleUtil.getLocalizedStr({
          string: locale.archive.unarchiveNotificationMessage,
          replacements: { title },
        }),
        { autoClose: FAST_NOTIFICATION_DURATION },
      );

      additionalUnarchiveActions?.();
      removeEvent?.(event);
      closeArchivePanel?.();
    } catch (error) {
      console.error(error);
      toast.error(locale.archive.errorToast);
    } finally {
      setIsLoading(false);
    }
  };

  const actions = [
    {
      text: locale.general.cancel,
      onClick: modalCloseAction,
      isPrimary: false,
      disabled: isLoading,
    },
    {
      text: locale.general.confirm,
      onClick: isArchiveContext ? archiveEvent : unarchiveEvent,
      disabled: isLoading,
    },
  ];

  const isShare = EventUtil.isShared(event);
  const isDownload = EventUtil.isDownload(event);
  const isClip = isShare || isDownload;
  const eventType = isClip ? locale.archive.clip : locale.archive.event;
  const archiveAction = isArchiveContext ? locale.archive.archive : locale.archive.unarchive;

  const archiveConfirmation = LocaleUtil.getLocalizedStr({
    string: locale.archive.archiveConfirmation,
    replacements: {
      eventType: eventType.toLowerCase(),
      archiveAction: archiveAction.toLowerCase(),
    },
  });

  const modalTitle = LocaleUtil.getLocalizedStr({
    string: locale.archive.archiveTitle,
    replacements: { eventType, archiveAction },
  });

  let infoText;

  if (isShare) {
    if (isArchiveContext) {
      infoText = locale.archive.archiveClipInfoText;
    } else {
      infoText = locale.archive.unarchiveClipInfoText;
    }
  }

  return (
    <Modal
      isOpen={archivePanelOpen}
      onClose={modalCloseAction}
      title={modalTitle}
      actions={actions}
    >
      <ModalContent>
        {isLoading ? (
          <CenteredLoadingSpinner />
        ) : (
          <TextWrapper>
            <div>{archiveConfirmation}</div>
            <EventTitle>{event.title}</EventTitle>
            {infoText && <InfoText>{infoText}</InfoText>}
          </TextWrapper>
        )}
      </ModalContent>
    </Modal>
  );
};

const TextWrapper = styled.div`
  font-size: 16px;
`;

const InfoText = styled.div`
  color: var(--color-dark-gray-lighter);
  font-size: 14px;
  margin-top: 16px;
`;

const EventTitle = styled.div`
  font-weight: 400;
  padding: 4px 0;
`;

const ModalContent = styled(Flex)`
  width: 100%;
  flex-direction: row;
  height: 100%;
`;

export default ArchivePanel;
